<template>
  <section class="notif-panel">
    <div class="notif-topline"></div>
    <div class="notif-heading">
      <div class="notif-label">Email</div>
      <h2>Notifications</h2>
      <p>Choose which emails GixCore sends you. Changes save as you flip them.</p>
    </div>

    <div v-if="loading" class="notif-empty">Loading your preferences…</div>
    <div v-else-if="error" class="notif-error">{{ error }}</div>
    <template v-else>
      <ul v-if="preferences.length" class="notif-list">
        <li v-for="pref in preferences" :key="pref.key" class="notif-row">
          <div class="notif-copy">
            <strong>{{ pref.label }}</strong>
            <small>{{ pref.description }}</small>
          </div>
          <span v-if="saved[pref.key]" class="notif-saved" aria-live="polite">Saved</span>
          <ToggleSwitch
            :value="pref.enabled"
            :busy="!!busy[pref.key]"
            :label="pref.label"
            :title="pref.enabled ? `Stop sending ${pref.label.toLowerCase()}` : `Send me ${pref.label.toLowerCase()}`"
            @change="toggle(pref, $event)" />
        </li>
      </ul>
      <p v-else class="notif-empty">There are no optional emails for your account yet.</p>

      <div v-if="alwaysSent.length" class="notif-always">
        <span>Always sent</span>
        <ul>
          <li v-for="item in alwaysSent" :key="item">{{ item }}</li>
        </ul>
      </div>
    </template>
  </section>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../../config'
import ToggleSwitch from './ToggleSwitch.vue'

/** Settings → Notifications: your own email preferences. */
export default {
  name: 'NotificationPreferencesPanel',
  components: { ToggleSwitch },
  data () {
    return {
      preferences: [],
      alwaysSent: [],
      loading: true,
      error: '',
      busy: {},
      saved: {},
      savedTimers: {}
    }
  },
  mounted () {
    this.load()
  },
  beforeDestroy () {
    Object.values(this.savedTimers).forEach(clearTimeout)
  },
  methods: {
    headers () {
      return { Authorization: `Bearer ${localStorage.usertoken}` }
    },
    apply (data) {
      this.preferences = (data && data.preferences) || []
      this.alwaysSent = (data && data.alwaysSent) || []
    },
    async load () {
      this.loading = true
      try {
        const { data } = await axios.get(`${apiDomain}notifications/preferences`, { headers: this.headers() })
        this.apply(data)
        this.error = ''
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
    },
    async toggle (pref, enabled) {
      this.$set(this.busy, pref.key, true)
      const previous = pref.enabled
      pref.enabled = enabled // show it at once; put it back if saving fails
      try {
        const { data } = await axios.put(`${apiDomain}notifications/preferences/${encodeURIComponent(pref.key)}`, { enabled }, { headers: this.headers() })
        this.apply(data)
        this.error = ''
        this.flashSaved(pref.key)
      } catch (e) {
        pref.enabled = previous
        this.error = this.errMsg(e)
      } finally {
        this.$delete(this.busy, pref.key)
      }
    },
    flashSaved (key) {
      this.$set(this.saved, key, true)
      if (this.savedTimers[key]) clearTimeout(this.savedTimers[key])
      this.savedTimers[key] = setTimeout(() => this.$delete(this.saved, key), 1800)
    },
    errMsg (e) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Something went wrong.'
    }
  }
}
</script>

<style scoped>
.notif-panel { position: relative; overflow: hidden; max-width: 760px; padding: 1.35rem 1.4rem 1.25rem; border: 1px solid rgba(69, 82, 104, 0.78); border-radius: 11px; background: radial-gradient(circle at 8% 0%, rgba(25, 151, 198, 0.1), transparent 34%), linear-gradient(145deg, rgba(16, 22, 32, 0.9), rgba(7, 11, 17, 0.9)); }
.notif-topline { position: absolute; top: 0; right: 0; left: 0; height: 2px; background: linear-gradient(90deg, #20b8eb, rgba(32, 184, 235, 0.18) 38%, transparent 78%); opacity: 0.8; }
.notif-label { color: #1997c6; font-size: 12px; font-weight: 700; letter-spacing: 0.8px; line-height: 1; text-transform: uppercase; }
.notif-heading h2 { margin: 0.4rem 0 0; color: #eef2f7; font-size: 20px; font-weight: 600; line-height: 1.2; }
.notif-heading p { margin: 0.4rem 0 0; color: #8994a8; font-size: 12px; line-height: 1.5; }
.notif-list { display: grid; margin: 1.1rem 0 0; padding: 0; list-style: none; }
.notif-row { display: flex; align-items: center; gap: 14px; padding: 13px 0; border-top: 1px solid rgba(69, 82, 104, 0.45); }
.notif-row:first-child { border-top: 0; }
.notif-copy { display: grid; flex: 1; gap: 3px; min-width: 0; }
.notif-copy strong { color: #e7ebf3; font-size: 13.5px; font-weight: 600; }
.notif-copy small { color: #8994a8; font-size: 12px; line-height: 1.45; }
.notif-saved { color: #8fe8c4; font-size: 11px; font-weight: 600; }
.notif-empty { margin: 1.1rem 0 0; color: #8994a8; font-size: 13px; }
.notif-error { margin-top: 1rem; padding: 9px 12px; border: 1px solid rgba(244, 154, 170, 0.4); border-radius: 7px; background: rgba(244, 154, 170, 0.08); color: #f49aaa; font-size: 12.5px; }
.notif-always { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 12px; margin-top: 1rem; padding-top: 0.9rem; border-top: 1px dashed rgba(69, 82, 104, 0.6); color: #8994a8; font-size: 12px; }
.notif-always > span { color: #aeb6c8; font-weight: 600; }
.notif-always ul { display: flex; flex-wrap: wrap; gap: 4px 14px; margin: 0; padding: 0; list-style: none; }
.notif-always li::before { margin-right: 6px; color: #4c5666; content: '•'; }
</style>
